import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { fetchPartnerCategory, deletePartnerCategory, deletePartnerCategoryCriteriaValue, fetchPartnerTypes } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function PartnerCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [partnerTypeName, setPartnerTypeName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/partner-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try {
      const [cat, types] = await Promise.all([
        fetchPartnerCategory(categoryId),
        fetchPartnerTypes(businessDomainId),
      ]);
      setData(cat);
      const found = (Array.isArray(types) ? types : []).find(
        (pt) => pt.partnerTypeId === cat.partnerTypeId
      );
      setPartnerTypeName(found?.partnerTypeName || "");
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      // 最新データを取得して分類基準値を全削除（外部キー制約対策）
      const freshCat = await fetchPartnerCategory(categoryId);
      const crValues = freshCat.classificationCriteriaValues || [];
      for (const val of crValues) {
        await deletePartnerCategoryCriteriaValue(categoryId, val);
      }
      await deletePartnerCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/partner-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("partnerCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("partnerCategory.partnerType")} value={partnerTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("partnerCategory.name")} value={data.partnerCategoryName} /></Grid>
          <Grid size={12}><DetailField label={t("common.description")} value={data.partnerCategoryDescription} /></Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<HandshakeIcon />}
          onClick={() => navigate(`${basePath}/partner-functions`)}
        >
          {t("partnerCategory.partnerFunctions")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AssessmentIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria`)}
        >
          {t("partnerCategory.classificationCriteriaValues")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/partner-categories`)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
